/* Deterministic usage & progress derivations for the 360 widgets. Real
 * time-series data would come from the usage_event object in production; here
 * we derive a stable weekly series from the account's health trend and active
 * seats so the Usage Activity and Progress widgets show a coherent story. */

export interface UsagePoint { label: string; active: number; events: number; }

/** 12 weeks of active users + events, scaled off the health trend. */
export function weeklyUsage(activeSeats: number, trend: number[]): UsagePoint[] {
  const peak = Math.max(1, ...trend);
  return trend.map((h, i) => {
    const active = Math.max(0, Math.round(activeSeats * (h / peak)));
    const events = active * (8 + (i % 5)); // deterministic per-week multiplier
    return { label: `W${i + 1}`, active, events };
  });
}

export interface UsageSummary { activeNow: number; wowPct: number; totalEvents: number; avgPerUser: number; }

export function usageSummary(points: UsagePoint[]): UsageSummary {
  const n = points.length;
  const activeNow = points[n - 1]?.active ?? 0;
  const prev = points[n - 2]?.active ?? activeNow;
  const wowPct = prev ? Math.round(((activeNow - prev) / prev) * 100) : 0;
  const totalEvents = points.reduce((s, p) => s + p.events, 0);
  const avgPerUser = activeNow ? Math.round((points[n - 1]?.events ?? 0) / activeNow) : 0;
  return { activeNow, wowPct, totalEvents, avgPerUser };
}

export interface Milestone { label: string; done: boolean; }
export interface Progress { onboarding: number; adoption: number; valueRealized: number; milestones: Milestone[]; }

/** Onboarding / adoption / value progress + milestone checklist. */
export function progressFor(opts: { implementationScore?: number; seats: number; activeSeats: number; health: number; goLive?: string }): Progress {
  const onboarding = Math.min(100, Math.round(opts.implementationScore ?? (opts.goLive ? 100 : 40)));
  const adoption = opts.seats ? Math.round((opts.activeSeats / opts.seats) * 100) : 0;
  const valueRealized = Math.round(opts.health);
  const milestones: Milestone[] = [
    { label: "Kickoff complete", done: true },
    { label: "Go-live", done: !!opts.goLive || onboarding >= 60 },
    { label: "Adoption target (60% seats)", done: adoption >= 60 },
    { label: "First value realized", done: valueRealized >= 55 },
    { label: "Expansion-ready", done: valueRealized >= 75 && adoption >= 70 },
  ];
  return { onboarding, adoption, valueRealized, milestones };
}
